import React, { useEffect, useState, useRef } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import './ToastNotification.css';

const ToastNotification: React.FC = () => {
  const { state } = useNotifications();
  const [toasts, setToasts] = useState<Array<{
    id: string;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    timestamp: number;
  }>>([]);
  const [popupNotification, setPopupNotification] = useState<{
    id: string;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
  } | null>(null);
  const processedNotifications = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (state.notifications.length > 0) {
      const latestNotification = state.notifications[0];
      
      // Evitar procesar la misma notificación múltiples veces
      if (processedNotifications.current.has(latestNotification.id)) {
        return;
      }
      
      // Marcar como procesada
      processedNotifications.current.add(latestNotification.id);
      
      // Determinar si es popup o toast basado en el tipo y contenido
      const isPopup = latestNotification.type === 'error' || 
                     latestNotification.title.toLowerCase().includes('error') ||
                     latestNotification.title.toLowerCase().includes('acceso') ||
                     latestNotification.title.toLowerCase().includes('contraseña') ||
                     latestNotification.title.toLowerCase().includes('denegado');

      if (isPopup) {
        // Mostrar como popup para errores críticos
        setPopupNotification({
          id: latestNotification.id,
          title: latestNotification.title,
          message: latestNotification.message,
          type: latestNotification.type,
        });
      } else {
        // Mostrar como toast para notificaciones informativas
        const newToast = {
          id: latestNotification.id,
          title: latestNotification.title,
          message: latestNotification.message,
          type: latestNotification.type,
          timestamp: Date.now(),
        };

        setToasts(prev => [...prev, newToast]);

        // Auto-remover después de 15 segundos (más tiempo para leer)
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== newToast.id));
        }, 15000);
      }
    }
  }, [state.notifications]);

  const closePopup = () => {
    setPopupNotification(null);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <>
      {/* Toast notifications (esquina superior derecha) */}
      {toasts.length > 0 && (
        <div className="toast-container" role="region" aria-live="polite" aria-atomic="true">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`toast toast-${toast.type}`}
              onClick={() => removeToast(toast.id)}
            >
              <div className="toast-header">
                <span className="toast-title">{toast.title}</span>
                <button 
                  className="toast-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeToast(toast.id);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="toast-message">{toast.message}</div>
            </div>
          ))}
        </div>
      )}

      {/* Popup modal para errores críticos */}
      {popupNotification && (
        <div className="notification-popup-overlay" onClick={closePopup}>
          <div className="notification-popup" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="popup-title" aria-describedby="popup-message">
            <div className={`notification-popup-header notification-popup-${popupNotification.type}`}>
              <span id="popup-title" className="notification-popup-title">{popupNotification.title}</span>
              <button className="notification-popup-close" onClick={closePopup}>
                ×
              </button>
            </div>
            <div id="popup-message" className="notification-popup-message">
              {popupNotification.message}
            </div>
            <div className="notification-popup-footer">
              <button className="notification-popup-btn" onClick={closePopup}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ToastNotification;
